import { hasNavigatorSendBeacon } from '../utils/safe-env';

export interface TransportConfig {
  apiUrl: string;
  apiKey: string;
  sdkVersion: string;
  timeoutMs?: number;
}

/** Error de transporte con el código HTTP asociado (0 si fue un fallo de red). */
export class TransportError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
  ) {
    super(message);
    this.name = 'TransportError';
  }
}

/**
 * Capa de red del SDK. Envía JSON al backend vía `fetch` (con timeout) y
 * ofrece `sendBeacon` para descargas fiables al cerrar la página.
 */
export class Transport {
  constructor(private readonly config: TransportConfig) {}

  /** Envío normal con `fetch`. Lanza `TransportError` si la respuesta no es OK. */
  async send<T = unknown>(path: string, body: unknown): Promise<T> {
    const url = `${this.config.apiUrl}${path}`;
    const controller = new AbortController();
    const timeoutMs = this.config.timeoutMs ?? 10000;
    // Abortamos con un motivo explícito para que el log sea claro
    // (`TimeoutError`) en vez del confuso "signal is aborted without reason".
    const timeoutId = setTimeout(
      () =>
        controller.abort(new DOMException(`Veo request timeout (${timeoutMs}ms)`, 'TimeoutError')),
      timeoutMs,
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.config.apiKey,
          'X-Sdk-Version': this.config.sdkVersion,
        },
        body: JSON.stringify(body),
        // NO usar `keepalive: true` en los flushes normales: tiene límites
        // estrictos del navegador (cuota de requests/64KB) y produce abortos
        // espurios cuando hay varios envíos seguidos. El envío fiable al cerrar
        // la página ya lo cubre `sendBeacon()` (ver Queue.flushBeacon).
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new TransportError(`HTTP ${response.status}`, response.status);
      }

      return (await response.json()) as T;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Envío con `navigator.sendBeacon`. Fire-and-forget, pensado para `pagehide`.
   * Como sendBeacon no admite headers, la API key viaja por query string.
   */
  sendBeacon(path: string, body: unknown): boolean {
    if (!hasNavigatorSendBeacon()) return false;

    const url = new URL(`${this.config.apiUrl}${path}`);
    url.searchParams.set('key', this.config.apiKey);
    url.searchParams.set('v', this.config.sdkVersion);

    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    return navigator.sendBeacon(url.toString(), blob);
  }
}
